import type { WorkActivity } from '@/lib/api/work-activity-api'
import { m } from '@/lib/paraglide/messages'

/**
 * Mốc "sắp bắt đầu": dưới ngưỡng này thì hàng chuyển sang hổ phách và đập nhẹ,
 * khớp với lời nhắc trước giờ họp 5 phút mà backend đã bắn.
 */
const SOON_MINUTES = 15

const MS_PER_MINUTE = 60_000

/** Giờ Việt Nam dạng HH:mm, không phụ thuộc múi giờ của máy chạy màn hình. */
export function vnTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Ngày Việt Nam dạng dd/MM — dùng cho lịch công tác kéo dài nhiều ngày. */
export function vnDay(iso: string): string {
    return new Date(iso).toLocaleDateString('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
    })
}

/** Khoá ngày theo lịch Việt Nam, để so "có phải hôm nay không". */
function vnDayKey(value: Date | string): string {
    const d = typeof value === 'string' ? new Date(value) : value
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Trạng thái một mục lịch so với thời điểm hiện tại. */
export type ScheduleState = 'past' | 'running' | 'soon' | 'upcoming'

/** Trạng thái một nhiệm vụ đến hạn hôm nay. */
export type TaskState = 'done' | 'overdue' | 'soon' | 'upcoming'

/** Ba nhóm nội dung của bảng, mỗi nhóm đọc theo một cách khác nhau. */
export interface StationWorkGroups {
    /**
     * Lịch họp trong ngày và sự kiện (hôm nay hoặc trong ~24 giờ tới — server
     * đã lọc và trả đúng lần diễn ra), xếp theo giờ bắt đầu.
     */
    schedule: WorkActivity[]
    /** Lịch công tác đang diễn ra (thường kéo dài nhiều ngày). */
    trips: WorkActivity[]
    /** Nhiệm vụ sắp đến hạn, gần nhất trước. Không bó trong ngày. */
    tasks: WorkActivity[]
}

/**
 * Chia hoạt động thành ba nhóm của bảng.
 *
 * Lịch công tác tách riêng vì nó kéo dài nhiều ngày — nhét vào dòng thời gian
 * trong ngày thì hoặc chiếm chỗ suốt cả cột giờ, hoặc phải cắt cụt thành một
 * mốc giả.
 */
export function groupStationWork(
    activities: WorkActivity[],
    now: Date,
): StationWorkGroups {
    const today = vnDayKey(now)
    const schedule: WorkActivity[] = []
    const trips: WorkActivity[] = []
    const tasks: WorkActivity[] = []

    for (const a of activities) {
        if (a.type === 'meeting' && a.starts_at) {
            if (vnDayKey(a.starts_at) === today) schedule.push(a)
            continue
        }
        // Sự kiện: server chỉ trả lần diễn ra hôm nay hoặc trong ~24 giờ tới,
        // để lời nhắc "ngày mai" có hàng trên bảng mà nhảy màn và tô sáng.
        // Không lọc lại theo "hôm nay" ở đây — lọc thì màn nhảy sang mà bảng
        // không có dòng nào.
        if (a.type === 'event' && a.starts_at) {
            schedule.push(a)
            continue
        }
        if (a.type === 'business_trip' && a.starts_at && a.ends_at) {
            // Còn đang diễn ra tính theo mốc kết thúc: chuyến bắt đầu từ hôm kia
            // mà chưa kết thúc thì hôm nay vẫn phải hiện.
            if (new Date(a.ends_at).getTime() >= now.getTime()) trips.push(a)
            continue
        }
        // Nhiệm vụ KHÔNG bó trong ngày: nhóm "sắp đến hạn" xếp theo hạn gần
        // nhất, để việc giao hôm nay cho ngày mai vẫn nhìn thấy được. Hạn chót
        // là `ends_at`. Việc đã xong không có chỗ trên dòng thời gian.
        if (
            a.type === 'task' &&
            a.ends_at &&
            a.status !== 'done' &&
            a.status !== 'closed'
        ) {
            tasks.push(a)
        }
    }

    schedule.sort(
        (x, y) =>
            new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime(),
    )
    trips.sort(
        (x, y) =>
            new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime(),
    )
    tasks.sort(
        (x, y) => new Date(x.ends_at).getTime() - new Date(y.ends_at).getTime(),
    )
    return { schedule, trips, tasks }
}

export function scheduleState(a: WorkActivity, now: Date): ScheduleState {
    const start = new Date(a.starts_at).getTime()
    const end = new Date(a.ends_at ?? a.starts_at).getTime()
    const at = now.getTime()
    if (at >= end) return 'past'
    if (at >= start) return 'running'
    if (start - at <= SOON_MINUTES * MS_PER_MINUTE) return 'soon'
    return 'upcoming'
}

export function taskState(a: WorkActivity, now: Date): TaskState {
    if (a.status === 'done' || a.status === 'closed') return 'done'
    const due = new Date(a.ends_at).getTime()
    const at = now.getTime()
    if (at >= due) return 'overdue'
    if (due - at <= SOON_MINUTES * MS_PER_MINUTE) return 'soon'
    return 'upcoming'
}

/**
 * Vị trí chèn vạch "BÂY GIỜ": trước mục đầu tiên chưa kết thúc.
 * Bằng độ dài danh sách nghĩa là mọi mục đã qua, vạch nằm cuối.
 */
export function nowLineIndex(schedule: WorkActivity[], now: Date): number {
    const idx = schedule.findIndex((a) => scheduleState(a, now) !== 'past')
    return idx === -1 ? schedule.length : idx
}

/**
 * Đếm ngược tới một mốc. Trả về chuỗi rỗng khi mốc đã qua.
 *
 * Chỉ mục KẾ TIẾP mới được đếm ngược; hàng nào cũng đếm thì cả bảng thành một
 * dãy số nhấp nháy, đọc từ xa không ra gì.
 */
export function countdownLabel(target: string, now: Date): string {
    const diff = new Date(target).getTime() - now.getTime()
    if (diff <= 0) return ''
    const totalMinutes = Math.ceil(diff / MS_PER_MINUTE)
    if (totalMinutes < 60) {
        return m.station_work_countdown_minutes({
            minutes: String(totalMinutes),
        })
    }
    return m.station_work_countdown_hours({
        hours: String(Math.floor(totalMinutes / 60)),
        minutes: String(totalMinutes % 60),
    })
}

/**
 * Nhãn cột người tham gia: một, hai người thì gọi tên; đông hơn thì đếm, vì
 * đứng xa không ai đọc hết một danh sách tên.
 */
export function peopleLabel(a: WorkActivity): string {
    const guests = a.guests?.length ?? 0
    // Khách ngoài luôn ĐẾM, không gọi tên: cột này rộng cố định, và tên khách
    // ngoài thường dài kèm chức vụ nên sẽ đẩy tên nhân viên ra khỏi khung.
    const suffix =
        guests > 0
            ? ` ${m.station_work_guests_suffix({ count: String(guests) })}`
            : ''

    // Giao toàn thể thì danh sách người để trống: phải nói "toàn thể", không
    // phải "chưa giao ai".
    if (a.assign_all) return m.station_work_all_staff() + suffix
    if (a.assignees.length === 0) {
        return guests > 0
            ? m.station_work_guests_suffix({ count: String(guests) })
            : m.station_work_nobody()
    }
    if (a.assignees.length <= 2) {
        return a.assignees.map((p) => p.full_name).join(', ') + suffix
    }
    return (
        m.station_work_people_count({ count: String(a.assignees.length) }) +
        suffix
    )
}

/**
 * Khoảng thời gian của một mục lịch: "08:00–09:30". Mục không thuộc hôm nay
 * (sự kiện ngày mai) kèm ngày phía trước, nếu không "10:00" của mai trông y
 * hệt "10:00" của hôm nay.
 */
export function scheduleTimeRange(a: WorkActivity, now?: Date): string {
    const start = vnTime(a.starts_at)
    const range = a.ends_at ? `${start}–${vnTime(a.ends_at)}` : start
    if (now && vnDayKey(a.starts_at) !== vnDayKey(now)) {
        return `${vnDay(a.starts_at)} ${range}`
    }
    return range
}

/**
 * Nhãn hạn của một nhiệm vụ.
 *
 * Cùng ngày thì chỉ cần giờ; khác ngày phải kèm ngày, nếu không "10:00" của mai
 * trông y hệt "10:00" của hôm nay và người đọc tưởng đã quá hạn.
 */
export function taskDueLabel(a: WorkActivity, now: Date): string {
    const due = a.ends_at
    if (vnDayKey(due) === vnDayKey(now)) return vnTime(due)
    return `${vnDay(due)} ${vnTime(due)}`
}

/** Khoảng ngày của một chuyến công tác: "07/09 → 09/09". */
export function tripDayRange(a: WorkActivity): string {
    return `${vnDay(a.starts_at)} → ${vnDay(a.ends_at)}`
}
